// Storage system for user data, settings, and attempts
// Handles localStorage persistence and retrieval

// Default settings
const DEFAULT_SETTINGS = {
  language: 'en',
  themeMode: 'light', // 'light', 'dark', or 'auto'
  bibleVersion: 'KJV',
  selectedBibleId: null,
  // Patent-aligned mastery metrics (replacing rigid masteryThreshold)
  masteryMetrics: {
    accuracyWeight: 60,           // Weight for accuracy in mastery score (50-100%)
    consistencyWeight: 25,        // Weight for consistency in mastery score (10-40%)
    progressionWeight: 15,         // Weight for improvement progression (5-30%)
    requiredMasteryScore: 80,     // Overall mastery score needed (70-100%)
    masteryLevels: {
      novice: { range: [0, 30], label: 'Novice', color: '#ff5722' },
      learning: { range: [30, 60], label: 'Learning', color: '#ff9800' },
      proficient: { range: [60, 80], label: 'Proficient', color: '#2196f3' },
      mastered: { range: [80, 100], label: 'Mastered', color: '#4caf50' }
    }
  },
  // Patent-aligned memorization settings
  patentSettings: {
    verseDisplayLevel: 'verse', // 'verse', 'sentence', 'phrase'
    removalMethod: 'time', // 'time' or 'repetitions'
    presentationTimeSeconds: 30,
    maxRepetitions: 3,
    realTimeEvaluation: true,
    detectSilence: true,
    silenceDetectionSeconds: 3,
    detectWordCount: true,
    responseMethod: 'highlight', // 'you_said', 'correct_response', 'highlight'
    progressCompilationType: 'progressive', // 'isolated' or 'progressive'
    accuracyThreshold: 80
  }
};

// Helper function to sanitize plan data
const sanitizePlan = (plan) => {
  return {
    ...plan,
    id: typeof plan.id === 'string' ? plan.id : plan.id?.toString() || 'unknown',
    name: typeof plan.name === 'string' ? plan.name : 'Unnamed Plan',
    description: typeof plan.description === 'string' ? plan.description : 'No description',
    verses: Array.isArray(plan.verses) ? plan.verses.filter(verse => typeof verse === 'string') : [],
    createdAt: typeof plan.createdAt === 'number' ? plan.createdAt : Date.now(),
  };
};

// Get settings from localStorage
export const getSettings = () => {
  try {
    const settings = localStorage.getItem('bibleMemorySettings');
    return settings ? { ...DEFAULT_SETTINGS, ...JSON.parse(settings) } : DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error loading settings:', error);
    return DEFAULT_SETTINGS;
  }
};

// Save settings to localStorage
export const saveSettings = (settings) => {
  try {
    localStorage.setItem('bibleMemorySettings', JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving settings:', error);
  }
};

// Get attempts from localStorage
export const getAttempts = () => {
  try {
    const attempts = localStorage.getItem('bibleMemoryAttempts');
    if (attempts) {
      return JSON.parse(attempts);
    } else {
      // If no attempts in localStorage, return empty array and initialize sample data
      setTimeout(() => {
        try {
          import('./../data/defaultData.json').then(data => {
            const sampleAttempts = data.attempts;
            if (sampleAttempts && sampleAttempts.length > 0 && !localStorage.getItem('bibleMemoryAttempts')) {
              localStorage.setItem('bibleMemoryAttempts', JSON.stringify(sampleAttempts));
              // Trigger a refresh of the component data if needed
              const event = new CustomEvent('sampleDataLoaded', { detail: { type: 'attempts' } });
              window.dispatchEvent(event);
            }
          }).catch(err => {
            console.warn('Could not load default data:', err);
          });
        } catch (error) {
          console.warn('Could not load default attempts:', error);
        }
      }, 0);
      return [];
    }
  } catch (error) {
    console.error('Error loading attempts:', error);
    return [];
  }
};

// Save attempt to localStorage
export const saveAttempt = (attempt) => {
  try {
    const attempts = getAttempts();
    attempts.push({ ...attempt, timestamp: Date.now() });
    localStorage.setItem('bibleMemoryAttempts', JSON.stringify(attempts));
  } catch (error) {
    console.error('Error saving attempt:', error);
  }
};

// Get plans from localStorage
export const getPlans = () => {
  try {
    const plans = localStorage.getItem('bibleMemoryPlans');
    if (plans) {
      const parsedPlans = JSON.parse(plans);
      // Sanitize each plan to handle corrupted data
      return Array.isArray(parsedPlans) ? parsedPlans.map(sanitizePlan) : [];
    } else {
      // For demo purposes, return default data directly
      try {
        import('./../data/defaultData.json').then(data => {
          const samplePlans = data.plans;
          if (samplePlans && samplePlans.length > 0 && !localStorage.getItem('bibleMemoryPlans')) {
            savePlans(samplePlans);
          }
        }).catch(err => {
          console.warn('Could not load default plans:', err);
        });
      } catch (error) {
        console.warn('Could not import default data:', error);
      }
      // Return default data directly for immediate display
      try {
        const data = require('./../data/defaultData.json');
        const defaultPlans = data.plans || [];
        // Sanitize default plans as well, in case they are corrupted
        return Array.isArray(defaultPlans) ? defaultPlans.map(sanitizePlan) : [];
      } catch (fallbackError) {
        console.warn('Could not load default plans:', fallbackError);
        return [];
      }
    }
  } catch (error) {
    console.error('Error loading plans:', error);
    return [];
  }
};

// Save plans to localStorage
export const savePlans = (plans) => {
  try {
    localStorage.setItem('bibleMemoryPlans', JSON.stringify(plans));
  } catch (error) {
    console.error('Error saving plans:', error);
  }
};

// Get added verses from localStorage
export const getAddedVerses = () => {
  try {
    const addedVerses = localStorage.getItem('bibleMemoryAddedVerses');
    return addedVerses ? JSON.parse(addedVerses) : [];
  } catch (error) {
    console.error('Error loading added verses:', error);
    return [];
  }
};

// Save added verses to localStorage
export const saveAddedVerses = (verses) => {
  try {
    localStorage.setItem('bibleMemoryAddedVerses', JSON.stringify(verses));
  } catch (error) {
    console.error('Error saving added verses:', error);
  }
};

// Patent-enhanced mastery calculation system
export const calculateMasteryMetrics = (attempts) => {
  if (!attempts || attempts.length === 0) {
    return {
      score: 0,
      level: 'novice',
      levelLabel: 'Novice',
      levelColor: '#ff5722',
      accuracy: 0,
      consistency: 0,
      progression: 0,
      factors: {
        accuracyFactor: 0,
        consistencyFactor: 0,
        progressionFactor: 0
      },
      details: {
        totalAttempts: 0,
        perfectAttempts: 0,
        averageAccuracy: 0,
        recentTrend: 'insufficient_data',
        learningCurve: 'flat'
      }
    };
  }

  const settings = getSettings();

  // Calculate weighted accuracy (average accuracy across all attempts)
  const totalAttempts = attempts.length;
  const averageAccuracy = attempts.reduce((sum, attempt) => sum + attempt.accuracy, 0) / totalAttempts;
  const perfectAttempts = attempts.filter(a => a.accuracy === 100).length;

  // Calculate consistency (variance in accuracy - lower is better)
  const accuracyVariance = attempts.reduce((sum, attempt) => {
    const diff = attempt.accuracy - averageAccuracy;
    return sum + (diff * diff);
  }, 0) / totalAttempts;
  const consistencyScore = Math.max(0, 100 - (accuracyVariance / 10)); // Normalize variance to 0-100 scale

  // Calculate progression (improvement over recent attempts)
  const recentAttempts = attempts.slice(-Math.min(10, attempts.length)); // Last 10 attempts
  const recentAverage = recentAttempts.reduce((sum, a) => sum + a.accuracy, 0) / recentAttempts.length;
  const earlyAttempts = attempts.slice(0, Math.min(10, Math.floor(attempts.length / 2)));
  const earlyAverage = earlyAttempts.length > 0 ? earlyAttempts.reduce((sum, a) => sum + a.accuracy, 0) / earlyAttempts.length : 0;

  let progressionScore = 50; // Default neutral
  if (earlyAttempts.length > 0) {
    const improvement = recentAverage - earlyAverage;
    if (improvement > 5) progressionScore = Math.min(100, 50 + (improvement * 2));
    else if (improvement < -5) progressionScore = Math.max(0, 50 + (improvement * 2));
  }

  // Apply weights and calculate final mastery score
  const accuracyFactor = (averageAccuracy * settings.masteryMetrics.accuracyWeight) / 100;
  const consistencyFactor = (consistencyScore * settings.masteryMetrics.consistencyWeight) / 100;
  const progressionFactor = (progressionScore * settings.masteryMetrics.progressionWeight) / 100;

  const totalScore = accuracyFactor + consistencyFactor + progressionFactor;

  // Determine mastery level
  const masteryLevels = settings.masteryMetrics.masteryLevels;
  let level = 'novice';
  let levelLabel = 'Novice';
  let levelColor = '#ff5722';

  if (totalScore >= masteryLevels.mastered.range[0]) {
    level = 'mastered';
    levelLabel = masteryLevels.mastered.label;
    levelColor = masteryLevels.mastered.color;
  } else if (totalScore >= masteryLevels.proficient.range[0]) {
    level = 'proficient';
    levelLabel = masteryLevels.proficient.label;
    levelColor = masteryLevels.proficient.color;
  } else if (totalScore >= masteryLevels.learning.range[0]) {
    level = 'learning';
    levelLabel = masteryLevels.learning.label;
    levelColor = masteryLevels.learning.color;
  }

  // Determine recent trend
  let recentTrend = 'stable';
  if (recentAttempts.length >= 3) {
    const lastThree = recentAttempts.slice(-3);
    const trend = lastThree[lastThree.length - 1].accuracy - lastThree[0].accuracy;
    if (trend > 10) recentTrend = 'improving';
    else if (trend < -10) recentTrend = 'declining';
  }

  // Determine learning curve
  let learningCurve = 'plateauing';
  if (earlyAttempts.length > 0 && totalAttempts > 20) {
    if (progressionScore > 70) learningCurve = 'growing';
    else if (progressionScore < 30) learningCurve = 'stagnating';
  }

  const isMastered = totalScore >= settings.masteryMetrics.requiredMasteryScore;

  return {
    score: Math.round(totalScore),
    level,
    levelLabel,
    levelColor,
    isMastered,
    accuracy: averageAccuracy,
    consistency: consistencyScore,
    progression: progressionScore,
    factors: {
      accuracyFactor: Math.round(accuracyFactor * 100) / 100,
      consistencyFactor: Math.round(consistencyFactor * 100) / 100,
      progressionFactor: Math.round(progressionFactor * 100) / 100
    },
    details: {
      totalAttempts,
      perfectAttempts,
      averageAccuracy: Math.round(averageAccuracy),
      recentTrend,
      learningCurve
    }
  };
};

// Simple cache system for API responses
export class Cache {
  static cache = new Map();

  static get(key) {
    const item = Cache.cache.get(key);
    if (item && Date.now() - item.timestamp < 24 * 60 * 60 * 1000) { // 24 hours expiry
      return item.data;
    }
    Cache.cache.delete(key);
    return null;
  }

  static set(key, data) {
    Cache.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  static clear() {
    Cache.cache.clear();
  }
}

// Add attempt to localStorage
export const addAttempt = (attemptData) => {
  try {
    const attempts = getAttempts();
    attempts.push({
      ...attemptData,
      timestamp: Date.now()
    });
    localStorage.setItem('bibleMemoryAttempts', JSON.stringify(attempts));
  } catch (error) {
    console.error('Error saving attempt:', error);
  }
};

/**
 * Splits text content into chunks based on patent-aligned strategies
 * @param {string} text - The text to chunk
 * @param {string} chunkType - 'words', 'phrases', 'sentences', 'verses'
 * @param {number} wordsPerChunk - Number of words per chunk (only for 'words' type)
 * @returns {string[]} Array of text chunks
 */
export const chunkContent = (text, chunkType, wordsPerChunk = 5) => {
  if (!text || text.trim().length === 0) {
    return [];
  }

  const trimmedText = text.trim();

  switch (chunkType) {
    case 'words':
      // Split by exact word count
      const words = trimmedText.split(/\s+/);
      const chunks = [];
      for (let i = 0; i < words.length; i += wordsPerChunk) {
        chunks.push(words.slice(i, i + wordsPerChunk).join(' '));
      }
      return chunks;

    case 'phrase':
      // Split by commas, semicolons, colons, em-dashes, and parentheses
      // Keep the punctuation as part of the chunk for natural reading
      const phraseChunks = trimmedText
        .split(/([,;:—()]+)/)
        .map(chunk => chunk.trim())
        .filter(chunk => chunk.length > 0);

      // Group small chunks back together to avoid too many tiny pieces
      const groupedPhrases = [];
      let currentGroup = '';

      for (const chunk of phraseChunks) {
        if (currentGroup.length === 0) {
          currentGroup = chunk;
        } else if (currentGroup.length + chunk.length < 20) { // Combine short chunks
          currentGroup += chunk;
        } else {
          groupedPhrases.push(currentGroup.trim());
          currentGroup = chunk;
        }
      }

      if (currentGroup.length > 0) {
        groupedPhrases.push(currentGroup.trim());
      }

      return groupedPhrases.length > 0 ? groupedPhrases : [trimmedText];

    case 'sentence':
      // Split by periods, question marks, exclamation points, commas, colons, semicolons, em-dashes
      // Clean up extra whitespace and filter empty chunks
      return trimmedText
        .split(/([.!?,;:—)]+)/)
        .map(chunk => chunk.trim())
        .filter(chunk => chunk.length > 0)
        .reduce((sentences, chunk, index, array) => {
          // Rejoin punctuation with preceding text
          if (index % 2 === 1) { // This is punctuation
            sentences[sentences.length - 1] += chunk;
          } else if (chunk.length > 0) {
            sentences.push(chunk);
          }
          return sentences;
        }, []);

    case 'verses':
      // No chunking - return entire verse as single chunk
      return [trimmedText];

    default:
      console.warn(`Unknown chunk type: ${chunkType}, falling back to words`);
      return chunkContent(text, 'words', wordsPerChunk);
  }
};

/**
 * Gets the text to display based on hierarchical verse display level
 * @param {string} text - The full verse text
 * @param {string} displayLevel - 'verse', 'sentence', or 'phrase'
 * @returns {string} Text formatted for display at the specified level
 */
export const getDisplayText = (text, displayLevel) => {
  if (!text || text.trim().length === 0) {
    return text;
  }

  const trimmedText = text.trim();

  switch (displayLevel) {
    case 'verse':
      // Return full verse
      return trimmedText;

    case 'sentence':
      // Always join sentences with line breaks for better readability
      const sentences = chunkContent(trimmedText, 'sentence');
      return sentences.join('\n');

    case 'phrase':
      // Always join phrases with line breaks for better readability
      const phrases = chunkContent(trimmedText, 'phrase');
      return phrases.join('\n');

    default:
      return trimmedText;
  }
};

/**
 * Fallback chunking when no punctuation exists in the text
 * Splits long text into roughly equal parts for better readability
 * @param {string} text - Text to split
 * @param {number} maxParts - Maximum number of parts to split into
 * @returns {string} Text with parts joined by spaces
 */
export const fallbackChunkText = (text, maxParts) => {
  const words = text.split(/\s+/);
  const wordsPerPart = Math.max(3, Math.ceil(words.length / maxParts));

  let result = '';
  for (let i = 0; i < words.length; i++) {
    if (i > 0 && i % wordsPerPart === 0 && i < words.length - 1) {
      result += '\n'; // Changed to line break for clearer separation
    }
    if (i > 0 && !(i % wordsPerPart === 0)) {
      result += ' ';
    }
    result += words[i];
  }

  return result || text;
};

/**
 * Gets recommended chunk size based on text length and difficulty
 * @param {string} text - The text to analyze
 * @returns {number} Recommended words per chunk
 */
export const getRecommendedChunkSize = (text) => {
  const wordCount = text.split(/\s+/).length;
  const avgWordLength = text.length / wordCount;

  // For longer words or complex passages, use smaller chunks
  if (avgWordLength > 6 || wordCount > 50) {
    return 3;
  } else if (wordCount > 30) {
    return 4;
  } else {
    return 5;
  }
};
